import { logError } from '@hotjar/insights-common/lib/logger';
import { FilterKindEnum } from '../../../../domains/global-filters/model/FilterChain';
import { getFilterMetadata } from '../../FilterChainUtils';
import { FilterTag } from '../GenericBloc/GenericFilter/FilterTag';
import { GenericRadioNumberFilter } from '../GenericBloc/GenericRadioNumberFilter/GenericRadioNumberFilter';
import { RadioNumberFilterOperation } from '../GenericBloc/GenericRadioNumberFilter/GenericRadioNumberType';
import { GenericRadioTextFilter } from '../GenericBloc/GenericRadioTextFilter/GenericRadioTextFilter';
import { GenericRadioDateFilter } from '../GenericBloc/GenericRadioDateFilter/GenericRadioDateFilter';
import { GenericRadioBooleanFilter } from '../GenericBloc/GenericRadioBooleanFilter/GenericRadioBooleanFilter';
import { RadioTextFilterOperation } from '../GenericBloc/GenericRadioTextFilter/GenericRadioTextType';
import type { AbstractSingleFilterPillsProps } from '../SingleFilterType';
import type { UserAttributeType } from './UserAttributeFilterType';
import { getIconName, isUserId } from './UserAttributeUtils';
import { RadioBooleanFilterOperation } from '../GenericBloc/GenericRadioBooleanFilter/GenericRadioBooleanType';
import { RadioDateFilterOperation } from '../GenericBloc/GenericRadioDateFilter/GenericRadioDateType';
import { UserAttributeKind } from '../../../../domains/user-attributes/model/UserAttribute';
import { getLabels } from '../../../../utils/LabelFormatter/SingleLabelFormatter/UserAttributeLabelFormatter';

const showUnknownUserAttributeKind = (never: never, onRemove?: () => void): JSX.Element => {
  logError(new Error(`Unknown User attribute kind (We shouldn't reach this part): ${never}`));
  return <FilterTag filterName="Unknown user attribute" iconName="warning" onRemove={onRemove} />;
};

interface UserAttributeWrapperProps extends AbstractSingleFilterPillsProps<UserAttributeType> {
  limitOperationsForUserId?: boolean;
}

export function UserAttributeWrapper({
  currentValue,
  onApply,
  onRemove,
  onDropdownHidden,
  showDropdownOnMount,
  limitOperationsForUserId,
}: UserAttributeWrapperProps): JSX.Element {
  const metadata = getFilterMetadata(FilterKindEnum.UserAttribute);
  const { label1, label2 } = getLabels(currentValue);

  const handleOnApply = (newDirtyValue: UserAttributeType) => {
    if (newDirtyValue != null) {
      onApply(newDirtyValue);
    }
  };

  if (!currentValue) {
    logError(new Error(`Unknown User attribute kind (We shouldn't reach this part)`));
    return <FilterTag filterName="Unknown user attribute" iconName="warning" onRemove={onRemove} />;
  }

  switch (currentValue?.kind) {
    case UserAttributeKind.Number: {
      return (
        <GenericRadioNumberFilter
          filterName={metadata.filterName}
          iconName={getIconName(currentValue.kind)}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            handleOnApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          options={[
            RadioNumberFilterOperation.IS_EXACTLY,
            RadioNumberFilterOperation.IS_NOT,
            RadioNumberFilterOperation.IS_LESS_THAN,
            RadioNumberFilterOperation.IS_GREATER_THAN,
            RadioNumberFilterOperation.IS_UNKNOWN,
            RadioNumberFilterOperation.HAS_ANY_VALUE,
          ]}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount ?? currentValue == null}
          label1={label1}
          label2={label2}
        />
      );
    }
    case UserAttributeKind.String: {
      const options =
        limitOperationsForUserId === true && isUserId(currentValue.name)
          ? [
              RadioTextFilterOperation.IS,
              RadioTextFilterOperation.IS_NOT,
              RadioTextFilterOperation.IS_UNKNOWN,
              RadioTextFilterOperation.HAS_ANY_VALUE,
            ]
          : [
              RadioTextFilterOperation.CONTAINS,
              RadioTextFilterOperation.NOT_CONTAINS,
              RadioTextFilterOperation.STARTS_WITH,
              RadioTextFilterOperation.ENDS_WITH,
              RadioTextFilterOperation.IS,
              RadioTextFilterOperation.IS_NOT,
              RadioTextFilterOperation.IS_UNKNOWN,
              RadioTextFilterOperation.HAS_ANY_VALUE,
            ];
      return (
        <GenericRadioTextFilter
          filterName={metadata.filterName}
          iconName={getIconName(currentValue.kind)}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            handleOnApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          options={options}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount ?? currentValue == null}
          label1={label1}
          label2={label2}
        />
      );
    }
    case UserAttributeKind.Email: {
      return (
        <GenericRadioTextFilter
          filterName={metadata.filterName}
          iconName={getIconName(currentValue.kind)}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            handleOnApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          options={[
            RadioTextFilterOperation.CONTAINS,
            RadioTextFilterOperation.NOT_CONTAINS,
            RadioTextFilterOperation.STARTS_WITH,
            RadioTextFilterOperation.ENDS_WITH,
            RadioTextFilterOperation.IS,
            RadioTextFilterOperation.IS_NOT,
            RadioTextFilterOperation.IS_UNKNOWN,
            RadioTextFilterOperation.HAS_ANY_VALUE,
          ]}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount ?? currentValue == null}
          label1={label1}
          label2={label2}
        />
      );
    }
    case UserAttributeKind.Boolean: {
      return (
        <GenericRadioBooleanFilter
          filterName={metadata.filterName}
          iconName={getIconName(currentValue.kind)}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            handleOnApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          options={[
            RadioBooleanFilterOperation.IS_TRUE,
            RadioBooleanFilterOperation.IS_FALSE,
            RadioBooleanFilterOperation.IS_UNKNOWN,
            RadioBooleanFilterOperation.HAS_ANY_VALUE,
          ]}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount ?? currentValue == null}
          label1={label1}
          label2={label2}
        />
      );
    }
    case UserAttributeKind.Date: {
      return (
        <GenericRadioDateFilter
          filterName={metadata.filterName}
          iconName={getIconName(currentValue.kind)}
          currentValue={currentValue.value}
          onApply={(newValue) =>
            handleOnApply({
              ...currentValue,
              value: newValue,
            })
          }
          onRemove={onRemove}
          options={[
            RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO,
            RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO,
            RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO,
            RadioDateFilterOperation.IS_ON,
            RadioDateFilterOperation.IS_AFTER,
            RadioDateFilterOperation.IS_BEFORE,
            RadioDateFilterOperation.IS_UNKNOWN,
            RadioDateFilterOperation.HAS_ANY_VALUE,
          ]}
          onDropdownHidden={onDropdownHidden}
          showDropdownOnMount={showDropdownOnMount ?? currentValue == null}
          label1={label1}
          label2={label2}
        />
      );
    }
    case UserAttributeKind.Unknown: {
      // We should not arrive here as we filter out unknown user attributes
      // But to avoid breaking the flow, we return a default value
      return (
        <FilterTag
          filterName={`${metadata.filterName} with unknown datatype`}
          iconName="warning"
          onRemove={onRemove}
        />
      );
    }
    default:
      return showUnknownUserAttributeKind(currentValue, onRemove);
  }
}
